/**
 * mapbox state
 * 
 * @Author: Focci
 * @Date: 2023-07-09 13:03:23
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-09 13:03:23
 */

import useStorage from '@hook/useStorage'
import { useCallback, useMemo, useRef, useState } from 'react'

export default function useMapboxState() {
  // (controller) 卫星图
  const [satellite, setSatellite] = useState(false)
  // (controller) 放大缩小
  const [navigation, setNavigation] = useState(true)
  // (controller) 全屏
  const [fullscreen, setFullscreen] = useState(false)
  // (marker) school
  const [school, setSchool] = useState(false)
  // (marker) 显示在售房源
  const [property, setProperty] = useState(false)
  // (marker) 显示售出
  const [sold, setSold] = useState(false)
  // (marker) 显示估价
  const [estimate, setEstimate] = useState(false)
  // (marker) 显示租房
  const [rental, setRental] = useState(false)
  // (marker) 显示农场房源
  const [rural, setRural] = useState(false)
  // (marker) 显示商业买卖房源
  const [commercialForSale, setCommercialForSale] = useState(false)
  // (marker) 显示商业租赁房源
  const [commercialForLease, setCommercialForLease] = useState(false)
  // (marker) 政府公屋
  const [stateHouse, setStateHouse] = useState(false)
  // (marker) 周边设施
  const [facility, setFacility] = useState('')
  // (marker) bus stops (array)
  const [busStops, setBusStops] = useState()
  // 显示地图 suburb 阴影 
  const [suburbShadow, setSuburbShadow] = useState({
    manual: false, // 是否手动设置过
    hoverBoundary: true, // 鼠标移动是否显示边界
    status: 2, // 状态: [0 不显示阴影组件, 1显示阴影, 2 隐藏阴影]
  })
  // 特殊的popup(比如:鼠标经过房源卡片，要在地图上显示的popup)
  const [specialPopup, setSpecialPopup] = useState()
  // (panel) 周边设施
  const [facilityPanel, setFacilityPanel] = useState(false)
  // (panel) 上班通勤
  const [traffic, setTraffic] = useState(false)
  // (panel) 是否显示周边学校面板
  const [schoolPanel, setSchoolPanel] = useState(false)
  // (panel)是否显示移动地图重新搜索面板
  const [moveResearch, setMoveResearch] = useState(false)
  // 是否已经勾选上移动重新搜索
  const [enabledMoveResearch, setEnabledMoveResearch] = useState(false)
  // 地图是否被移动过
  const [isMapMoved, setIsMapMoved] = useState(false)
  /**
   * 保存地图接口返回的数据，及请求状态
   * 格式为:
   * { 
   *   buy: { 
   *     loading: true, // 是否正在请求接口
   *     total: 0, // 总的数量
   *     displayCount: 0, // 显示的数量
   *   },
   *   rent: {},
   *   ....
   * }
   */
  const [mapData, setMapData] = useState({})
  // 过滤school的参数
  const [schoolParam, setSchoolParam] = useState({
    type: [],
    minDecile: 0
  })

  /**
   * popup数据
   * data的lng, lat 为必须参数
   * [{
   *   type: '类型',
   *   popupProps: {}, popup properties http://visgl.github.io/react-map-gl/docs/api-reference/popup#properties
   *   data: { lng, lat, .... }
   * }, ....]
   */
  const [popups, setPopups] = useState([])

  // 已经查看过的 Markers
  // 保存地图上的marker查看状态
  const vms = useStorage('VIEWED_MARKERS_ID', true)
  const [viewedMarkers, setViewedMarkers] = useState(vms.getItem() || [])
  const updateViewedMarker = useCallback((v) => {
    const tmp = [v, ...viewedMarkers.slice(0, 2000)]
    setViewedMarkers([v, ...viewedMarkers.slice(0, 2000)])
    vms.setItem(tmp)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedMarkers])
  
  // 临时数据(zoom-n-center接口获取)
  const [extra, setExtra] = useState({
    // stateId: 洲(澳洲),
    // position: suburb, district
  })
  /**
   * 以后图层(layer)数据都由后端统一返回
   * https://test3.hougarden.com/api/v5.1.6/map-layers
   */
  const [layers, setLayers] = useState({
    // 当前需要显示的layers
    display: [],
    // map-layers 接口返回的数据
    cfg: null,
  })

  /**
   * events
   * 保存地图上卡片、链接等点击事件
   */
  const [events, setEvents] = useState({
    // onSchoolPanelSearch: () => {}, 点击地图上学校卡片搜索房源按钮
  })

  /**
   * 用来锁定某些操作
   */
  const locker = useRef({
    suburbShadowClick: false, // 是否点击mapbox上的 suburblayer
    // 是否是主动移动地图, 用于移动地图后更新地图上的点
    // 会则会出现移动后因提前请求而没有数据的情况
    mapboxMoveProactive: false,
  })

  return useMemo(() => ({
    satellite,
    setSatellite,
    fullscreen,
    setFullscreen,
    navigation,
    setNavigation,
    school,
    setSchool,
    property,
    setProperty,
    sold,
    setSold,
    estimate,
    setEstimate,
    rental,
    setRental,
    rural, 
    setRural,
    commercialForSale, 
    setCommercialForSale,
    commercialForLease, 
    setCommercialForLease,
    stateHouse,
    setStateHouse,
    facility,
    setFacility,
    busStops,
    setBusStops,
    suburbShadow,
    setSuburbShadow,
    specialPopup,
    setSpecialPopup,
    facilityPanel,
    setFacilityPanel,
    traffic,
    setTraffic,
    schoolPanel,
    setSchoolPanel,
    moveResearch,
    setMoveResearch,
    enabledMoveResearch,
    setEnabledMoveResearch,
    mapData,
    setMapData,
    isMapMoved,
    setIsMapMoved,
    schoolParam,
    setSchoolParam,
    popups,
    setPopups,
    viewedMarkers,
    setViewedMarkers,
    updateViewedMarker,
    extra,
    setExtra,
    layers,
    setLayers,
    events,
    setEvents,
    locker,
  }), [
    busStops, commercialForLease, commercialForSale, enabledMoveResearch, 
    estimate, extra, facility, facilityPanel, fullscreen, isMapMoved, layers, 
    mapData, navigation, popups, property, rental, rural, satellite, 
    school, schoolPanel, moveResearch, schoolParam, sold, specialPopup, stateHouse, traffic, 
    viewedMarkers, suburbShadow, events,
    updateViewedMarker, setLayers
  ])
}
